import { Input, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { SearchableSelect, SelectSize } from '../Select'

interface IBrazilBank {
  code: number | null
  name: string
  fullName: string
}

export interface IBankSelectProps {
  /** Código do banco (ex.: '341'). */
  code: string | null
  /** Nome do banco. É o que sobra quando a lista não carrega. */
  name: string | null
  onChange: (code: string | null, name: string | null) => void
  isDisabled?: boolean
  /**
   * `sm` só no formulário de linhas repetidas (os estabelecimentos do evento), onde todo campo do
   * cartão é `sm` — a mesma exceção do `<FormSelect>`. Fora dele, não passe.
   */
  size?: SelectSize
}

const BANKS_URL = 'https://brasilapi.com.br/api/banks/v1'

/**
 * Só bancos de verdade, um por código, ordenados pelo código.
 *
 * A lista da BrasilAPI traz as câmaras da B3 com `code: 0` — três entradas que não são
 * destino de pagamento de ninguém e que, por compartilharem o código, viravam três opções
 * com a mesma chave de lista: o React deixava de atualizar os primeiros resultados da busca
 * e ia acumulando itens presos a cada nova digitação. Deduplicar por código também protege
 * de qualquer repetição futura da fonte.
 */
function usableBanks(data: IBrazilBank[]): IBrazilBank[] {
  const byCode = new Map<number, IBrazilBank>()

  data.forEach((bank) => {
    // Descarta code null e o 0 das câmaras: código COMPE de banco começa em 1.
    if (!bank.code || byCode.has(bank.code)) return
    byCode.set(bank.code, bank)
  })

  return Array.from(byCode.values()).sort((a, b) => (a.code ?? 0) - (b.code ?? 0))
}

// Uma requisição por sessão: o modal do cupom abre e fecha várias vezes seguidas.
let banksPromise: Promise<IBrazilBank[]> | null = null

function loadBanks(): Promise<IBrazilBank[]> {
  if (!banksPromise) {
    banksPromise = fetch(BANKS_URL)
      .then((res) => {
        // Um erro da BrasilAPI costuma vir com corpo que não é a lista: sem estas duas
        // checagens a falha só aparecia como um TypeError lá dentro do usableBanks().
        if (!res.ok) throw new Error(`BrasilAPI respondeu ${res.status}`)
        return res.json()
      })
      .then((data: IBrazilBank[]) => {
        if (!Array.isArray(data)) throw new Error('BrasilAPI devolveu um formato inesperado')
        return usableBanks(data)
      })
      .catch((error) => {
        // Sem cache do erro: a próxima abertura do modal tenta de novo.
        banksPromise = null
        throw error
      })
  }

  return banksPromise
}

export const bankLabel = (code: string | null, name: string | null): string => [code, name].filter(Boolean).join(' - ')

/**
 * Seletor de banco alimentado pela lista da BrasilAPI, gravando código e nome.
 *
 * Se a lista não carregar — ou se o valor já gravado não estiver nela —, vira um campo de
 * texto livre com o nome do banco: um serviço externo fora do ar não pode travar o
 * cadastro dentro do painel.
 */
export default function BankSelect({ code, name, onChange, isDisabled = false, size }: IBankSelectProps) {
  const [banks, setBanks] = useState<IBrazilBank[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true

    loadBanks()
      .then((data) => active && setBanks(data))
      .catch(() => active && setFailed(true))
      .finally(() => active && setLoading(false))

    return () => {
      active = false
    }
  }, [])

  const options = useMemo(
    () => banks.map((bank) => ({ value: String(bank.code), label: `${bank.code} - ${bank.fullName}` })),
    [banks]
  )

  // Um banco gravado que não existe na lista (digitado antes, ou lista fora do ar) não pode
  // sumir da tela só porque o select não sabe representá-lo.
  const isUnlisted = !loading && !!name && !options.some((option) => option.value === code)

  if (failed || isUnlisted) {
    return (
      <>
        <Input
          value={name ?? ''}
          onChange={(e) => onChange(code, e.target.value || null)}
          placeholder="EX: 341 - Itaú Unibanco S.A."
          maxLength={255}
          isDisabled={isDisabled}
          size={size}
        />
        {failed && (
          <Text variant="caption" mt={1}>
            Não foi possível carregar a lista de bancos — digite o nome do banco.
          </Text>
        )}
      </>
    )
  }

  return (
    <SearchableSelect
      options={options}
      value={code ?? ''}
      onChange={(value) => {
        const bank = banks.find((b) => String(b.code) === value)
        onChange(bank ? String(bank.code) : null, bank ? bank.fullName : null)
      }}
      placeholder="Selecione o banco"
      entity="banco"
      isDisabled={isDisabled}
      isLoading={loading}
      isClearable
      size={size}
    />
  )
}
